import careConfig from "@careConfig";
import { useMutation } from "@tanstack/react-query";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useQueryParams } from "raviger";
import { useEffect, useState } from "react";
import ReCaptcha from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { PasswordInput } from "@/components/ui/input-password";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { ForgotPasswordPanel } from "@/components/Auth/ForgotPasswordPanel";
import CircularProgress from "@/components/Common/CircularProgress";
import LanguageSelectorLogin from "@/components/Common/LanguageSelectorLogin";

import { useAuthContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";

import FiltersCache from "@/Utils/FiltersCache";
import ViewCache from "@/Utils/ViewCache";
import mutate from "@/Utils/request/mutate";
import { HTTPError } from "@/Utils/request/types";
import authApi from "@/types/auth/authApi";
import { TokenData } from "@/types/otp/otp";
import otpApi from "@/types/otp/otpApi";

import { clearQueryPersistenceCache } from "@/Utils/request/queryClient";
import { invalidateAllPaymentReconcilationLocationCaches } from "@/atoms/paymentReconcilationLocationAtom";
import { clearQueuePractitionerCache } from "@/atoms/queuePractitionerAtom";
import { AuthHero } from "./AuthHero";
import { LabBrand } from "./LabBrand";

interface OtpLoginData {
  phone_number: string;
  otp: string;
}

interface OtpError {
  type: string;
  loc: string[];
  msg: string;
  input: string;
  ctx: {
    error: string;
  };
  url: string;
}

interface OtpValidationError {
  otp?: string;
  [key: string]: string | undefined;
}

type LoginMode = "staff" | "patient";

interface LoginProps {
  forgot?: boolean;
}

const Login = (props: LoginProps) => {
  const { signIn, patientLogin, isAuthenticating } = useAuthContext();
  const {
    reCaptchaSiteKey,
    mainLogo,
    stateLogo,
    customLogo,
    appName,
    resendOtpTimeout,
    disablePatientLogin,
  } = careConfig;
  const initForm: any = {
    username: "",
    password: "",
  };
  const { forgot } = props;
  const [params, setQueryParams] = useQueryParams();
  const { mode } = params;
  const initErr: any = {};
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState(initErr);
  const [isCaptchaEnabled, setCaptcha] = useState(false);
  const { t } = useTranslation();
  const [forgotPassword, setForgotPassword] = useState(forgot);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string>("");
  const [otpValidationError, setOtpValidationError] = useState<string>("");
  const [resendOtpCountdown, setResendOtpCountdown] = useState(0);

  // Timer Function for resend OTP
  useEffect(() => {
    if (resendOtpCountdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendOtpCountdown((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendOtpCountdown]);

  // Remember the last login mode
  useEffect(() => {
    localStorage.setItem(LocalStorageKeys.loginPreference, mode);
  }, [mode]);

  // Send OTP Mutation
  const { mutate: sendOtp, isPending: sendOtpPending } = useMutation({
    mutationFn: mutate(otpApi.send),
    onSuccess: () => {
      setIsOtpSent(true);
      setOtpError("");
      toast.success(t("send_otp_success"));
    },
    onError: (error: any) => {
      const errors = error?.data || [];
      if (Array.isArray(errors) && errors.length > 0) {
        const firstError = errors[0] as OtpError;
        setOtpError(firstError.msg);
      } else {
        setOtpError("send_otp_error");
      }
    },
  });

  // Verify OTP Mutation
  const { mutate: verifyOtp, isPending: verifyOtpPending } = useMutation({
    mutationFn: async (data: OtpLoginData) => {
      const response = await mutate(otpApi.login, { silent: true })(data);
      if ("errors" in response) {
        throw response;
      }
      return response;
    },
    onSuccess: async (response: { access: string }) => {
      const { access } = response;
      if (access) {
        setOtpValidationError("");
        const tokenData: TokenData = {
          token: access,
          phoneNumber: phone,
          createdAt: new Date().toISOString(),
        };
        patientLogin(tokenData, `/patient/home`);
      }
    },
    onError: (error: any) => {
      let errorMessage = "invalid_otp";
      if (
        error.cause &&
        Array.isArray(error.cause.errors) &&
        error.cause.errors.length > 0
      ) {
        const otpError = error.cause.errors.find(
          (e: OtpValidationError) => e.otp,
        );
        if (otpError && otpError.otp) {
          errorMessage = otpError.otp;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setOtpValidationError(errorMessage);
      toast.error(errorMessage);
    },
  });

  // Forgot Password Mutation
  const { mutate: submitForgetPassword, isPending: forgotPasswordPending } =
    useMutation({
      mutationFn: mutate(authApi.forgotPassword),
      onSuccess: () => {
        toast.success(t("password_sent"));
      },
    });

  // Login form validation
  const handleChange = (e: any) => {
    const { value, name } = e.target;
    const fieldValue = Object.assign({}, form);
    const errorField = Object.assign({}, errors);
    if (errorField[name]) {
      errorField[name] = null;
      setErrors(errorField);
    }
    fieldValue[name] = value;
    if (name === "username") {
      fieldValue[name] = value.toLowerCase();
    }
    setForm(fieldValue);
  };

  const validateData = () => {
    let hasError = false;
    const err = Object.assign({}, errors);
    Object.keys(form).forEach((key) => {
      if (
        typeof form[key] === "string" &&
        key !== "password" &&
        key !== "confirm"
      ) {
        if (!form[key].match(/\w/)) {
          hasError = true;
          err[key] = "field_required";
        }
      }
      if (!form[key]) {
        hasError = true;
        err[key] = "field_required";
      }
    });
    if (hasError) {
      setErrors(err);
      return false;
    }
    return form;
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    ViewCache.invalidateAll();
    const validated = validateData();
    if (!validated) return;

    FiltersCache.invalidateAll();
    invalidateAllPaymentReconcilationLocationCaches();
    clearQueuePractitionerCache();
    clearQueryPersistenceCache();
    try {
      await signIn(validated);
    } catch (error) {
      if (error instanceof HTTPError) {
        setCaptcha(error.status == 429);
      }
    }
  };

  const validateForgetData = () => {
    let hasError = false;
    const err = Object.assign({}, errors);

    if (typeof form.username === "string") {
      if (!form.username.match(/\w/)) {
        hasError = true;
        err.username = "field_required";
      }
    }
    if (!form.username) {
      hasError = true;
      err.username = "field_required";
    }

    if (hasError) {
      setErrors(err);
      return false;
    }
    return form;
  };
  const handleForgetSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const valid = validateForgetData();
    if (!valid) return;

    submitForgetPassword(valid);
  };

  const onCaptchaChange = (value: any) => {
    if (value && isCaptchaEnabled) {
      const formCaptcha = { ...form };
      formCaptcha["g-recaptcha-response"] = value;
      setForm(formCaptcha);
    }
  };

  // Handle OTP flow
  const handlePatientLogin = async (e: React.SubmitEvent) => {
    e.preventDefault();

    if (!isOtpSent) {
      sendOtp({ phone_number: phone });
      setResendOtpCountdown(resendOtpTimeout);
    } else {
      verifyOtp({ phone_number: phone, otp });
    }
  };

  const resetPatientLogin = () => {
    setIsOtpSent(false);
    setPhone("");
    setOtp("");
    setOtpError("");
    setOtpValidationError("");
  };

  // Loading state derived from mutations
  const isLoading = isAuthenticating || sendOtpPending || verifyOtpPending;

  const logos = [stateLogo, customLogo].filter(
    (logo) => logo?.light || logo?.dark,
  );

  return (
    <div className="relative flex min-h-screen flex-col md:h-screen md:flex-row">
      <AuthHero />

      {/* Login Forms Section */}
      <div className="login-hero-form my-4 w-full md:mt-0 md:h-full md:w-1/2">
        <div className="relative h-full items-center flex justify-center md:flex">
          <div className="w-full max-w-[400px] space-y-6">
            {/* Logo for Mobile */}
            <div className="px-4 flex items-center mx-auto gap-4 md:hidden">
              {logos.map((logo, index) =>
                logo && logo.dark ? (
                  <div key={index} className="flex items-center">
                    <img
                      src={logo.dark}
                      className="h-14 rounded-lg py-3"
                      alt=""
                    />
                  </div>
                ) : null,
              )}
              <img src={mainLogo?.dark} className="h-9 w-auto" alt={appName} />
            </div>
            <LabBrand />
            <Card className="mx-4">
              <CardHeader className="space-y-1 px-4">
                <CardTitle className="text-2xl font-bold">
                  {t("welcome_back")}
                </CardTitle>
                <CardDescription>
                  {disablePatientLogin
                    ? t("sign_in_to_your_account_to_continue")
                    : t("choose_your_login_method_to_continue")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {disablePatientLogin ? (
                  <>
                    {/* Staff Login */}
                    {!forgotPassword ? (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="username">{t("username")}</Label>
                          <Input
                            id="username"
                            name="username"
                            type="text"
                            autoComplete="username"
                            value={form.username}
                            onChange={handleChange}
                            className={cn(
                              errors.username &&
                                "border-red-500 focus-visible:ring-red-500",
                            )}
                          />
                          {errors.username && (
                            <p className="text-sm text-red-500">
                              {t(errors.username)}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="password">{t("password")}</Label>
                          <PasswordInput
                            id="password"
                            name="password"
                            autoComplete="current-password"
                            value={form.password}
                            onChange={handleChange}
                            className={cn(
                              errors.password &&
                                "border-red-500 focus-visible:ring-red-500",
                            )}
                          />
                          {errors.password && (
                            <p className="text-sm text-red-500">
                              {t(errors.password)}
                            </p>
                          )}
                        </div>

                        {isCaptchaEnabled && reCaptchaSiteKey && (
                          <div className="py-4">
                            <ReCaptcha
                              sitekey={reCaptchaSiteKey}
                              onChange={onCaptchaChange}
                            />
                          </div>
                        )}

                        <Button
                          variant="link"
                          type="button"
                          onClick={() => setForgotPassword(true)}
                          className="px-0"
                        >
                          {t("forget_password")}
                        </Button>

                        <Button
                          type="submit"
                          className="w-full"
                          variant="primary"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <CircularProgress className="text-white" />
                          ) : (
                            t("login")
                          )}
                        </Button>
                      </form>
                    ) : (
                      <ForgotPasswordPanel
                        username={form.username}
                        usernameError={errors.username}
                        onUsernameChange={handleChange}
                        onSubmitEmail={handleForgetSubmit}
                        onBackToLogin={() => setForgotPassword(false)}
                        isSubmitting={isLoading || forgotPasswordPending}
                      />
                    )}
                  </>
                ) : (
                  <Tabs
                    value={mode === "patient" ? "patient" : "staff"}
                    onValueChange={(value) => {
                      setQueryParams({ mode: value as LoginMode });
                      if (value === "staff") {
                        resetPatientLogin();
                      } else {
                        setForgotPassword(false);
                      }
                    }}
                  >
                    <TabsList className="flex w-full">
                      <TabsTrigger className="flex-1" value="staff">
                        {t("staff_login")}
                      </TabsTrigger>
                      {!disablePatientLogin && (
                        <TabsTrigger className="flex-1" value="patient">
                          {t("patient_login")}
                        </TabsTrigger>
                      )}
                    </TabsList>

                    {/* Staff Login */}
                    <TabsContent value="staff">
                      {!forgotPassword ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="username">{t("username")}</Label>
                            <Input
                              id="username"
                              name="username"
                              type="text"
                              autoComplete="username"
                              value={form.username}
                              onChange={handleChange}
                              className={cn(
                                errors.username &&
                                  "border-red-500 focus-visible:ring-red-500",
                              )}
                            />
                            {errors.username && (
                              <p className="text-sm text-red-500">
                                {t(errors.username)}
                              </p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="password">{t("password")}</Label>
                            <PasswordInput
                              id="password"
                              name="password"
                              autoComplete="current-password"
                              value={form.password}
                              onChange={handleChange}
                              className={cn(
                                errors.password &&
                                  "border-red-500 focus-visible:ring-red-500",
                              )}
                            />
                            {errors.password && (
                              <p className="text-sm text-red-500">
                                {t(errors.password)}
                              </p>
                            )}
                          </div>

                          {isCaptchaEnabled && reCaptchaSiteKey && (
                            <div className="py-4">
                              <ReCaptcha
                                sitekey={reCaptchaSiteKey}
                                onChange={onCaptchaChange}
                              />
                            </div>
                          )}

                          <Button
                            variant="link"
                            type="button"
                            onClick={() => setForgotPassword(true)}
                            className="px-0"
                          >
                            {t("forget_password")}
                          </Button>

                          <Button
                            type="submit"
                            className="w-full"
                            variant="primary"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <CircularProgress className="text-white" />
                            ) : (
                              t("login")
                            )}
                          </Button>
                        </form>
                      ) : (
                        <ForgotPasswordPanel
                          username={form.username}
                          usernameError={errors.username}
                          onUsernameChange={handleChange}
                          onSubmitEmail={handleForgetSubmit}
                          onBackToLogin={() => setForgotPassword(false)}
                          isSubmitting={isLoading || forgotPasswordPending}
                        />
                      )}
                    </TabsContent>

                    {/* Patient Login */}
                    <TabsContent value="patient">
                      <form onSubmit={handlePatientLogin} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">{t("phone_number")}</Label>
                          <PhoneInput
                            id="phone"
                            name="phone"
                            value={phone}
                            onChange={(value) => {
                              setPhone(value ?? "");
                              setOtpError("");
                              setOtpValidationError("");
                            }}
                            disabled={isOtpSent}
                            placeholder={t("enter_phone_number")}
                          />
                          {otpError && (
                            <p className="text-sm text-red-500">
                              {t(otpError)}
                            </p>
                          )}
                        </div>

                        {isOtpSent && (
                          <div className="space-y-2">
                            <Label htmlFor="otp" className="mb-4">
                              {t("enter_otp")}
                            </Label>
                            <div className="flex justify-center">
                              <InputOTP
                                id="otp"
                                value={otp}
                                maxLength={5}
                                pattern={REGEXP_ONLY_DIGITS}
                                autoComplete="one-time-code"
                                autoFocus
                                onChange={(value) => {
                                  setOtp(value);
                                  setOtpValidationError("");
                                }}
                              >
                                <InputOTPGroup>
                                  {[...Array(5)].map((_, index) => (
                                    <InputOTPSlot
                                      key={index}
                                      index={index}
                                      className={cn(
                                        "size-10",
                                        otpValidationError &&
                                          "border-red-500 focus-visible:ring-red-500",
                                      )}
                                    />
                                  ))}
                                </InputOTPGroup>
                              </InputOTP>
                            </div>
                            {otpValidationError && (
                              <p className="text-sm text-red-500 text-center">
                                {t(otpValidationError)}
                              </p>
                            )}
                          </div>
                        )}

                        <Button
                          type="submit"
                          className="w-full"
                          variant="primary"
                          disabled={
                            isLoading ||
                            !isValidPhoneNumber(phone) ||
                            (isOtpSent && otp.length !== 5)
                          }
                        >
                          {isLoading ? (
                            <CircularProgress className="text-white" />
                          ) : isOtpSent ? (
                            t("verify_otp")
                          ) : (
                            t("send_otp")
                          )}
                        </Button>
                        {isOtpSent && (
                          <div className="flex flex-col items-center gap-2 text-center">
                            {resendOtpCountdown <= 0 ? (
                              <Button
                                variant="link"
                                type="button"
                                className="h-auto p-0"
                                onClick={() => {
                                  sendOtp({ phone_number: phone });
                                  setResendOtpCountdown(resendOtpTimeout);
                                }}
                              >
                                {t("resend_otp")}
                              </Button>
                            ) : (
                              <p className="text-sm text-gray-500">
                                {t("resend_otp_timer", {
                                  time: resendOtpCountdown,
                                })}
                              </p>
                            )}
                            <div className="flex items-center text-sm">
                              <Button
                                variant="link"
                                type="button"
                                className="h-auto p-0 text-primary-600"
                                onClick={() => {
                                  setIsOtpSent(false);
                                  setOtp("");
                                  setOtpError("");
                                  setOtpValidationError("");
                                }}
                              >
                                {t("change_phone_number")}
                              </Button>
                            </div>
                          </div>
                        )}
                      </form>
                    </TabsContent>
                  </Tabs>
                )}
              </CardContent>
            </Card>

            <LanguageSelectorLogin />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
